/* eslint-disable security/detect-unsafe-regex */

const socialRegex = {
  appleMusic: /^https?:\/\/music\.apple\.com\/[a-z]{2}\/(artist|album|playlist)\/[A-Za-z0-9-]+\/?(\\?.*)?$/,
  bereal: /^https?:\/\/(www\.)?bere\.al\/[A-Za-z0-9_.-]+(\/[A-Za-z0-9_.-]+)?\/?$/,
  discord: /^https?:\/\/(www\.)?(discord(?:app)?\.com\/users\/[A-Za-z0-9]+|discord\.gg\/[A-Za-z0-9]+)\/?$/,
  facebook: /^https?:\/\/(www\.)?(facebook\.com|fb\.com)\/([A-Za-z0-9.]{1,}|pages\/[A-Za-z0-9_-]+)\/?$/,
  instagram: /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_.]+\/?$/,
  linkedin: /^https?:\/\/(www\.)?linkedin\.com\/(in|school|company)\/[A-Za-z0-9_%.-]+\/?$/,
  pinterest: /^https?:\/\/(www\.)?pinterest\.[a-z.]+\/[A-Za-z0-9_-]+\/?$/,
  podimo: /^https?:\/\/(www\.)?podimo\.com\/[a-z]{2}\/shows\/[A-Za-z0-9_-]+\/?$/,
  polarsteps: /^https?:\/\/(www\.)?polarsteps\.com\/[A-Za-z0-9_-]+\/?$/,
  snapchat: /^https?:\/\/(www\.)?snapchat\.com\/@([A-Za-z0-9._]+)\/?$/,
  soundcloud:
    /^https?:\/\/(www\.)?soundcloud\.com\/([A-Za-z0-9_-]+|tracks\/[A-Za-z0-9_-]+|playlists\/[A-Za-z0-9_-]+)\/?$/,
  spotify: /^https?:\/\/(open|play)\.spotify\.com\/([a-z]+)\/[A-Za-z0-9_-]+\/?(\\?.*)?$/,
  threads: /^https?:\/\/(www\.)?threads\.net\/@([A-Za-z0-9._]+)\/?$/,
  tiktok: /^https?:\/\/(www\.)?tiktok\.com\/@([A-Za-z0-9._]{2,24})\/?$/,
  twitch: /^https?:\/\/(www\.)?twitch\.tv\/[A-Za-z0-9_]{4,25}\/?$/,
  twitter: /^https?:\/\/(www\.)?(twitter|x)\.com\/[A-Za-z0-9_]{1,15}\/?$/,
  vinted: /^https?:\/\/(www\.)?vinted\.[a-z.]+\/members?\/[A-Za-z0-9_.]+\/?$/,
  youtube:
    /^https?:\/\/(www\.)?(youtube\.com\/(@[A-Za-z0-9._-]+|(c|channel|user)\/[A-Za-z0-9_-]+)|youtu\.be\/[A-Za-z0-9_-]+)\/?$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

export interface BasePlatformConfig {
  platform: string;
  platformName: string;
  domains: string[];
  validation: (url: string) => boolean;
}

export const basePlatformConfigs = [
  {
    platform: "instagram",
    platformName: "Instagram",
    domains: ["instagram.com"],
    validation: (url: string) => socialRegex.instagram.test(url),
  },
  {
    platform: "tiktok",
    platformName: "TikTok",
    domains: ["tiktok.com"],
    validation: (url: string) => socialRegex.tiktok.test(url),
  },
  {
    platform: "youtube",
    platformName: "YouTube",
    domains: ["youtube.com", "youtu.be"],
    validation: (url: string) => socialRegex.youtube.test(url),
  },
  {
    platform: "snapchat",
    platformName: "Snapchat",
    domains: ["snapchat.com"],
    validation: (url: string) => socialRegex.snapchat.test(url),
  },
  {
    platform: "pinterest",
    platformName: "Pinterest",
    domains: ["pinterest.com", "pin.it"],
    validation: (url: string) => socialRegex.pinterest.test(url),
  },
  {
    platform: "x",
    platformName: "X",
    domains: ["x.com", "twitter.com"],
    validation: (url: string) => socialRegex.twitter.test(url),
  },
  {
    platform: "twitch",
    platformName: "Twitch",
    domains: ["twitch.tv"],
    validation: (url: string) => socialRegex.twitch.test(url),
  },
  {
    platform: "linkedin",
    platformName: "LinkedIn",
    domains: ["linkedin.com"],
    validation: (url: string) => socialRegex.linkedin.test(url),
  },
  {
    platform: "facebook",
    platformName: "Facebook",
    domains: ["facebook.com", "fb.com"],
    validation: (url: string) => socialRegex.facebook.test(url),
  },
  {
    platform: "spotify",
    platformName: "Spotify",
    domains: ["spotify.com", "open.spotify.com"],
    validation: (url: string) => socialRegex.spotify.test(url),
  },
  {
    platform: "soundcloud",
    platformName: "SoundCloud",
    domains: ["soundcloud.com"],
    validation: (url: string) => socialRegex.soundcloud.test(url),
  },
  {
    platform: "podimo",
    platformName: "Podimo",
    domains: ["podimo.com"],
    validation: (url: string) => socialRegex.podimo.test(url),
  },
  {
    platform: "apple-music",
    platformName: "Apple Music",
    domains: ["music.apple.com"],
    validation: (url: string) => socialRegex.appleMusic.test(url),
  },
  {
    platform: "vinted",
    platformName: "Vinted",
    domains: ["vinted.com"],
    validation: (url: string) => socialRegex.vinted.test(url),
  },
  {
    platform: "threads",
    platformName: "Threads",
    domains: ["threads.net", "threads.com"],
    validation: (url: string) => socialRegex.threads.test(url),
  },
  {
    platform: "bereal",
    platformName: "BeReal",
    domains: ["bere.al", "bereal.com"],
    validation: (url: string) => socialRegex.bereal.test(url),
  },
  {
    platform: "polarsteps",
    platformName: "Polarsteps",
    domains: ["polarsteps.com"],
    validation: (url: string) => socialRegex.polarsteps.test(url),
  },
  {
    platform: "discord",
    platformName: "Discord",
    domains: ["discord.com", "discord.gg"],
    validation: (url: string) => socialRegex.discord.test(url),
  },
  {
    platform: "email",
    platformName: "Email",
    domains: [""],
    validation: (value: string) => socialRegex.email.test(value),
  },
] as const satisfies Array<BasePlatformConfig>;

export type SupportedPlatform = (typeof basePlatformConfigs)[number]["platform"];

export const emailValidation = (value: string) => socialRegex.email.test(value);
